"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { authApi, toApiError } from "@/lib/api";
import { ensureSamlSession, navigateAfterSamlLogin } from "@/lib/auth";
import { toast } from "sonner";
import {
  isSamlCallbackPath,
  redirectLegacyCallbackIfNeeded,
} from "@/lib/auth/saml-config";
import {
  clearLoginErrorFromLocation,
  formatLoginErrorMessage,
  readLoginErrorFromLocation,
  redirectToLoginWithError,
} from "@/lib/auth/login-error";
import { clearSignInPending } from "@/lib/auth/sign-in-pending";

function normalizePath(pathname: string): string {
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function isLoginPath(pathname: string): boolean {
  const normalized = normalizePath(pathname);
  return normalized === "/" || normalized.startsWith("/login/");
}

/** Complete SAML handoff and send authenticated users away from login routes. */
export default function LoginSessionBootstrap({
  children,
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const onLoginRoute = isLoginPath(pathname);
  const onCallbackRoute = isSamlCallbackPath(pathname);

  useEffect(() => {
    if (redirectLegacyCallbackIfNeeded()) {
      return;
    }

    if (onCallbackRoute) {
      return;
    }

    // Login form owns `?error=` display — do not re-process as a SAML handoff.
    if (onLoginRoute && readLoginErrorFromLocation()) {
      return;
    }

    async function bootstrap() {
      try {
        await authApi.initializeAuth();

        const authenticated = await ensureSamlSession();
        if (onLoginRoute && authenticated) {
          clearSignInPending();
          navigateAfterSamlLogin();
        }
      } catch (err) {
        clearSignInPending();
        const message = formatLoginErrorMessage(
          toApiError(err, "sign-in").message,
        );

        if (onLoginRoute) {
          clearLoginErrorFromLocation();
          toast.error(message);
          return;
        }

        redirectToLoginWithError(message);
      }
    }

    void bootstrap();
  }, [pathname, onLoginRoute, onCallbackRoute]);

  return children;
}
